import displayConditions from "../../../data/displayConditionsObj";
import {
  CONNECT_SPLASH_BOOLEAN_FIELDS,
  CONNECT_SPLASH_FIELD_KEYS,
  CONNECT_SPLASH_TRUE_DEFAULTS,
  CONNECT_SPLASH_TYPE,
  isConnectSplashType,
  unpackConnectSplashFromItem,
} from "../../../data/connectSplashConfig";
import {
  JOIN_INVITE_BOOLEAN_FIELDS,
  JOIN_INVITE_FIELD_KEYS,
  JOIN_INVITE_TRUE_DEFAULTS,
  JOIN_INVITE_TYPE,
  isJoinInviteType,
  unpackJoinInviteFromItem,
} from "../../../data/joinInviteConfig";

export const SYSTEM_FIELDS = [
  "_id",
  "identifier",
  "masterLibraryID",
  "createdAt",
  "updatedAt",
  "draftPayload",
  "publishedAt",
];

export const isTruthyFlag = (value) => {
  if (value === true || value === 1) return true;
  if (value === false || value === 0 || value == null) return false;
  if (typeof value === "string") {
    const v = value.trim().toLowerCase();
    return v === "true" || v === "1" || v === "yes";
  }
  return Boolean(value);
};

export const itemsToArray = (itemsObj) => {
  if (!itemsObj || typeof itemsObj !== "object") return [];
  return Object.entries(itemsObj).map(([key, item]) => ({
    ...item,
    _storeKey: key,
    _id: item?._id || key,
  }));
};

/** Preferred type order for catalog, then content; unknown types sort A–Z after. */
const TYPE_ORDER = [
  "song",
  "album",
  "playlist",
  "service",
  "about",
  "noticeOne",
  "noticeTwo",
  "noticeThree",
  "socialConnection",
  "menuOrder",
  CONNECT_SPLASH_TYPE,
  JOIN_INVITE_TYPE,
];

/** Sort type keys: preferred order first, then A–Z, untagged last. */
export const sortTypeKeys = (types) =>
  Array.from(types).sort((a, b) => {
    const ia = TYPE_ORDER.indexOf(a);
    const ib = TYPE_ORDER.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    if (a === "untagged") return 1;
    if (b === "untagged") return -1;
    return a.localeCompare(b);
  });

export const getUniqueTypes = (items) => {
  const types = new Set();
  items.forEach((item) => {
    if (item?.type && String(item.type).trim()) {
      types.add(String(item.type).trim());
    }
  });
  return sortTypeKeys(types);
};

export const countByType = (items) => {
  const counts = {};
  items.forEach((item) => {
    const t = item?.type ? String(item.type).trim() : "unknown";
    counts[t] = (counts[t] || 0) + 1;
  });
  return counts;
};

export const groupItemsByType = (items) => {
  const map = new Map();
  (items || []).forEach((item) => {
    const raw = item?.type != null ? String(item.type).trim() : "";
    const key = raw || "untagged";
    if (!map.has(key)) map.set(key, []);
    map.get(key).push(item);
  });

  const keys = sortTypeKeys(map.keys());

  return keys.map((type) => ({
    type,
    items: map.get(type) || [],
  }));
};

export const countActive = (items) =>
  items.filter((item) => isTruthyFlag(item?.active)).length;

export const filterAndSortItems = (
  items,
  { search = "", type = "", active = "", sortKey = "title", sortDir = "asc" } = {},
) => {
  const q = search.trim().toLowerCase();
  let result = items.filter((item) => {
    if (type && String(item.type || "").trim() !== type) return false;
    if (active === "active" && !isTruthyFlag(item.active)) return false;
    if (active === "inactive" && isTruthyFlag(item.active)) return false;
    if (active === "draft") {
      const raw = item?.draftPayload;
      if (raw == null || String(raw).trim() === "") return false;
    }
    if (!q) return true;
    const haystack = [
      item.title,
      item.slug,
      item.type,
      item.artist,
      item.name,
      item.identifier,
      item.tags,
      item.link,
      item.profileLink,
      item._id,
    ]
      .filter(Boolean)
      .join(" ")
      .toLowerCase();
    return haystack.includes(q);
  });

  result = [...result].sort((a, b) => {
    let av = a[sortKey];
    let bv = b[sortKey];
    if (sortKey === "active") {
      av = isTruthyFlag(av) ? 1 : 0;
      bv = isTruthyFlag(bv) ? 1 : 0;
    }
    if (av == null) av = "";
    if (bv == null) bv = "";
    if (typeof av === "string") av = av.toLowerCase();
    if (typeof bv === "string") bv = bv.toLowerCase();
    if (av < bv) return sortDir === "asc" ? -1 : 1;
    if (av > bv) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  return result;
};

export const paginate = (items, page = 1, pageSize = 25) => {
  const total = items.length;
  const totalPages = Math.max(1, Math.ceil(total / pageSize) || 1);
  const safePage = Math.min(Math.max(1, page), totalPages);
  const start = (safePage - 1) * pageSize;
  return {
    page: safePage,
    pageSize,
    total,
    totalPages,
    items: items.slice(start, start + pageSize),
  };
};

/**
 * Catalog fields the public UI supports even if the API schema is older.
 * - preReleaseMessage: upcoming-release copy on songs/albums/playlists
 * - defaultEmbedQuery: service-level embed URL query defaults (overlay at play)
 */
export const CATALOG_UI_EXTRA_FIELDS = [
  "preReleaseMessage",
  "defaultEmbedQuery",
];

/** Service form extras rendered with custom UI (not the generic text field). */
export const SERVICE_SPECIAL_FIELDS = [
  "iframeCustomAttributes",
  "defaultEmbedQuery",
];

export const DEFAULT_CATALOG_TYPES = ["song", "album", "playlist", "service"];
export const DEFAULT_CONTENT_TYPES = [
  "about",
  "noticeOne",
  "noticeTwo",
  "noticeThree",
  "socialConnection",
  "menuOrder",
  CONNECT_SPLASH_TYPE,
  JOIN_INVITE_TYPE,
];

/**
 * Canonical theme key for item types (CSS: item-type-song, item-type-about, …).
 * Unknown types return "" so no tint is applied.
 * @param {string|null|undefined} type
 * @returns {string} lowercase theme key or ""
 */
export const getItemTypeThemeKey = (type) => {
  const t = String(type || "")
    .trim()
    .toLowerCase();
  if (DEFAULT_CATALOG_TYPES.includes(t)) return t;
  // Content types (normalize camelCase → single token for CSS)
  const contentTheme = {
    about: "about",
    noticeone: "noticeone",
    noticetwo: "noticetwo",
    noticethree: "noticethree",
    socialconnection: "social",
    menuorder: "menuorder",
    connectsplash: "connectsplash",
    joininvite: "joininvite",
  };
  return contentTheme[t] || "";
};

const MUSIC_ITEM_TYPES = ["song", "album", "playlist"];
const NOTICE_LIKE_TYPES = [
  "about",
  "noticeOne",
  "noticeTwo",
  "noticeThree",
];
const SOCIAL_TYPES = ["socialConnection"];
const MENU_ORDER_TYPES = ["menuOrder"];
const CONNECT_SPLASH_TYPES = [CONNECT_SPLASH_TYPE];
const JOIN_INVITE_TYPES = [JOIN_INVITE_TYPE];

/** Image + body slots used on Notice / About / NoticeThree public layouts */
const NOTICE_IMAGE_AND_TEXT_FIELDS = [
  "text",
  "textTwo",
  "textThree",
  "textFour",
  "imageOneURL",
  "imageOneAttribution",
  "imageOneAttributionURL",
  "imageOneCSS",
  "imageTwoURL",
  "imageTwoAttribution",
  "imageTwoAttributionURL",
  "imageTwoCSS",
  "imageThreeURL",
  "imageThreeAttribution",
  "imageThreeAttributionURL",
  "imageThreeCSS",
  "imageFourURL",
  "imageFourAttribution",
  "imageFourAttributionURL",
  "imageFourCSS",
];

/**
 * Fields only relevant to certain item types.
 * Keys not listed here are shown for every type (title, active, etc.).
 * Unknown / custom types show all fields so nothing is locked away.
 */
export const CATALOG_TYPE_RESTRICTED_FIELDS = {
  sourceURLObj: MUSIC_ITEM_TYPES,
  artist: MUSIC_ITEM_TYPES,
  releaseDate: MUSIC_ITEM_TYPES,
  realeaseDate: MUSIC_ITEM_TYPES, // legacy typo field if present in schema
  preReleaseMessage: MUSIC_ITEM_TYPES,
  tags: MUSIC_ITEM_TYPES,
  description: MUSIC_ITEM_TYPES,
  notes: MUSIC_ITEM_TYPES,
  credits: MUSIC_ITEM_TYPES,
  about: MUSIC_ITEM_TYPES,
  linerNotes: MUSIC_ITEM_TYPES,
  story: MUSIC_ITEM_TYPES,
  // Home welcome player default — any listenable item (name is historical)
  isDefaultPlaylist: MUSIC_ITEM_TYPES,
  isFeaturedPlaylist: MUSIC_ITEM_TYPES,
  iframeCustomAttributes: ["service"],
  defaultEmbedQuery: ["service"],
  isDefaultService: ["service"],
  // Services need a stable slug for embed maps / URL columns
  slug: ["service", ...MUSIC_ITEM_TYPES],
};

/**
 * Content form field visibility by type.
 * Shared (always shown): type, title, active, slug, system fields.
 * Notice-like: full image/text blocks + nav options.
 * Social: profile link/icon fields + nav option.
 * Menu order: menuOrder only (plus shared).
 * Connect splash: Join / Kit confirmation copy + flags (plus shared).
 * Join invite: homepage email popup copy, cover, and timing (plus shared).
 */
export const CONTENT_TYPE_RESTRICTED_FIELDS = {
  // Notice / About body + images
  ...Object.fromEntries(
    NOTICE_IMAGE_AND_TEXT_FIELDS.map((f) => [f, NOTICE_LIKE_TYPES]),
  ),
  // About can also use a generic link; social uses profileLink/link
  link: ["about", "socialConnection"],
  tags: NOTICE_LIKE_TYPES,
  titleOnNavMenu: [...NOTICE_LIKE_TYPES, "socialConnection"],
  addToNavMenu: [...NOTICE_LIKE_TYPES, "socialConnection"],
  // Social connections
  name: SOCIAL_TYPES,
  faIcon: SOCIAL_TYPES,
  profileLink: SOCIAL_TYPES,
  iconURL: SOCIAL_TYPES,
  // Menu order config
  menuOrder: MENU_ORDER_TYPES,
  // Join / Kit confirmation screens
  ...Object.fromEntries(
    CONNECT_SPLASH_FIELD_KEYS.map((f) => [f, CONNECT_SPLASH_TYPES]),
  ),
  ...Object.fromEntries(
    JOIN_INVITE_FIELD_KEYS.map((f) => [f, JOIN_INVITE_TYPES]),
  ),
};

/**
 * Whether a model field should appear for the given item type.
 * @param {string} fieldName
 * @param {string} itemType
 * @param {"catalog"|"content"} section
 */
export const isFieldVisibleForItemType = (
  fieldName,
  itemType,
  section = "catalog",
) => {
  if (!fieldName) return false;
  // System fields are gated by the "Show system fields" toggle, not type
  if (isSystemField(fieldName)) return true;

  const type = String(itemType || "")
    .trim()
    .toLowerCase();
  const rules =
    section === "content"
      ? CONTENT_TYPE_RESTRICTED_FIELDS
      : CATALOG_TYPE_RESTRICTED_FIELDS;
  const allowed = rules[fieldName];
  if (!allowed) return true;

  // No type chosen yet — still show type-restricted fields so create isn't empty
  if (!type) return true;

  const known = (
    section === "content" ? DEFAULT_CONTENT_TYPES : DEFAULT_CATALOG_TYPES
  ).map((t) => String(t).toLowerCase());
  // Custom / legacy types: show everything so admins aren't blocked
  // (must compare case-insensitively — noticeOne → "noticeone")
  if (!known.includes(type)) return true;

  return allowed.map((t) => String(t).toLowerCase()).includes(type);
};

/**
 * Prefer a sensible field order in admin forms.
 * Notice/About content follows the public layout:
 *   title → image One (URL → credits → CSS) → text → link →
 *   image Two + textTwo → image Three + textThree → image Four + textFour
 * (Alphabet alone puts imageFour* before imageOne* and Attribution before URL.)
 */
const FIELD_DISPLAY_PRIORITY = [
  "type",
  "title",
  "active",
  "slug",
  "artist",
  "releaseDate",
  "preReleaseMessage",
  "tags",
  "sourceURLObj",
  "isFeaturedPlaylist",
  "isDefaultPlaylist",
  "isDefaultService",
  "iframeCustomAttributes",
  "defaultEmbedQuery",
  "description",
  "notes",
  "credits",
  // Content block 1 — hero image + lead copy (NoticeOne / About / NoticeThree)
  "imageOneURL",
  "imageOneAttribution",
  "imageOneAttributionURL",
  "imageOneCSS",
  "text",
  "link",
  // Content block 2
  "imageTwoURL",
  "imageTwoAttribution",
  "imageTwoAttributionURL",
  "imageTwoCSS",
  "textTwo",
  // Content block 3
  "imageThreeURL",
  "imageThreeAttribution",
  "imageThreeAttributionURL",
  "imageThreeCSS",
  "textThree",
  // Content block 4
  "imageFourURL",
  "imageFourAttribution",
  "imageFourAttributionURL",
  "imageFourCSS",
  "textFour",
  // Social / nav
  "name",
  "faIcon",
  "profileLink",
  "menuOrder",
  "addToNavMenu",
  ...CONNECT_SPLASH_FIELD_KEYS,
  ...JOIN_INVITE_FIELD_KEYS,
];

/** Word-number slots used on image* and text* CMS fields */
const SLOT_RANK = {
  one: 1,
  two: 2,
  three: 3,
  four: 4,
};

/** Within an image slot: URL first, then credit text, credit link, then CSS */
const IMAGE_PART_RANK = {
  url: 1,
  attribution: 2,
  attributionurl: 3,
  css: 4,
};

/**
 * Stable sort key for fields not listed in FIELD_DISPLAY_PRIORITY.
 * Keeps imageOne* before imageFour* and URL before Attribution.
 */
const naturalFieldSortKey = (name) => {
  const raw = String(name || "");
  const imageMatch = raw.match(
    /^image(One|Two|Three|Four)(URL|AttributionURL|Attribution|CSS)$/i,
  );
  if (imageMatch) {
    const slot = SLOT_RANK[imageMatch[1].toLowerCase()] || 9;
    const part = IMAGE_PART_RANK[imageMatch[2].toLowerCase()] || 9;
    return `image-${slot}-${part}-${raw.toLowerCase()}`;
  }
  const textMatch = raw.match(/^text(One|Two|Three|Four)?$/i);
  if (textMatch) {
    // bare "text" = lead / text one
    const slotWord = textMatch[1] ? textMatch[1].toLowerCase() : "one";
    const slot = SLOT_RANK[slotWord] || 1;
    return `text-${slot}-${raw.toLowerCase()}`;
  }
  return `z-${raw.toLowerCase()}`;
};

export const sortFieldsForDisplay = (keys) => {
  const list = [...keys];
  list.sort((a, b) => {
    const ia = FIELD_DISPLAY_PRIORITY.indexOf(a);
    const ib = FIELD_DISPLAY_PRIORITY.indexOf(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return naturalFieldSortKey(a).localeCompare(naturalFieldSortKey(b));
  });
  return list;
};

export const CONTENT_UI_EXTRA_FIELDS = [
  ...CONNECT_SPLASH_FIELD_KEYS,
  ...JOIN_INVITE_FIELD_KEYS,
];

export const getModelFieldKeys = (model, { section } = {}) => {
  if (!model || typeof model !== "object") {
    if (section === "catalog") return [...CATALOG_UI_EXTRA_FIELDS];
    if (section === "content") return [...CONTENT_UI_EXTRA_FIELDS];
    return [];
  }
  const keys = Object.keys(model).filter((k) => k !== "$timestamps");
  if (section === "catalog") {
    CATALOG_UI_EXTRA_FIELDS.forEach((field) => {
      if (!keys.includes(field)) keys.push(field);
    });
  }
  if (section === "content") {
    CONTENT_UI_EXTRA_FIELDS.forEach((field) => {
      if (!keys.includes(field)) keys.push(field);
    });
  }
  // Prefer pre-release message near releaseDate when both exist
  const releaseIdx = keys.indexOf("releaseDate");
  const preIdx = keys.indexOf("preReleaseMessage");
  if (releaseIdx >= 0 && preIdx > releaseIdx + 1) {
    keys.splice(preIdx, 1);
    keys.splice(releaseIdx + 1, 0, "preReleaseMessage");
  }
  return keys;
};

/**
 * Model field keys visible for this item type (excludes system unless requested).
 */
export const getVisibleModelFieldKeys = (
  model,
  { section = "catalog", itemType = "", includeSystem = false } = {},
) => {
  const keys = getModelFieldKeys(model, { section }).filter((k) => {
    if (isSystemField(k)) return includeSystem;
    return isFieldVisibleForItemType(k, itemType, section);
  });
  return sortFieldsForDisplay(keys);
};

export const getRequiredFields = (model) => {
  const required = [];
  if (!model) return required;
  for (const key of getModelFieldKeys(model)) {
    if (model[key] && model[key].required) required.push(key);
  }
  return required;
};

/** Required fields that are actually shown for this type. */
export const getRequiredFieldsForItem = (
  model,
  itemType,
  section = "catalog",
) =>
  getRequiredFields(model).filter((field) =>
    isFieldVisibleForItemType(field, itemType, section),
  );

export const getFieldKind = (fieldName, conditionsKey = "formWithPreFilledData") => {
  const cond = displayConditions[conditionsKey] || displayConditions.formWithPreFilledData;
  // Nested service-URL map must win over isURL (sourceURLObj is listed there for legacy forms).
  if (fieldName === "sourceURLObj") return "sourceURLObj";
  if (CONNECT_SPLASH_BOOLEAN_FIELDS.includes(fieldName)) return "boolean";
  if (JOIN_INVITE_BOOLEAN_FIELDS.includes(fieldName)) return "boolean";
  if (cond.isBoolean?.includes(fieldName)) return "boolean";
  if (cond.isDate?.includes(fieldName)) return "date";
  if (cond.isURL?.includes(fieldName)) return "url";
  if (cond.richTextEditorNeeded?.includes(fieldName)) return "richtext";
  return "text";
};

export const isSystemField = (fieldName) => SYSTEM_FIELDS.includes(fieldName);

export const buildBlankItem = (
  model,
  { services = [], type = "", section = "catalog" } = {},
) => {
  const blank = {};
  getModelFieldKeys(model, { section }).forEach((key) => {
    if (key === "sourceURLObj") {
      blank[key] = {};
      services.filter(Boolean).forEach((s) => {
        blank[key][s] = "";
      });
    } else if (key === "type" && type) {
      blank[key] = type;
    } else if (getFieldKind(key) === "boolean") {
      blank[key] =
        CONNECT_SPLASH_TRUE_DEFAULTS.has(key) ||
        JOIN_INVITE_TRUE_DEFAULTS.has(key)
          ? "true"
          : "false";
    } else {
      blank[key] = "";
    }
  });
  return blank;
};

/** Normalize sourceURLObj whether it arrives as object, JSON string, or missing. */
export const normalizeSourceURLObj = (raw) => {
  if (!raw) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) {
    return { ...raw };
  }
  if (typeof raw === "string") {
    const trimmed = raw.trim();
    if (!trimmed || trimmed === "[object Object]") return {};
    try {
      const parsed = JSON.parse(trimmed);
      if (parsed && typeof parsed === "object" && !Array.isArray(parsed)) {
        return { ...parsed };
      }
    } catch {
      return {};
    }
  }
  return {};
};

export const prepareItemWithServices = (item, services = []) => {
  // Plain clone so RTK/Immer proxies don't hide fields from form controls
  const next = item && typeof item === "object" ? { ...item } : {};
  const allServices = services.filter(Boolean);
  next.sourceURLObj = normalizeSourceURLObj(next.sourceURLObj);
  allServices.forEach((s) => {
    if (!Object.hasOwn(next.sourceURLObj, s)) next.sourceURLObj[s] = "";
  });
  // Ensure UI-only catalog fields are always editable as plain strings.
  // Coerce so textarea always receives a string (never null/undefined/object).
  CATALOG_UI_EXTRA_FIELDS.forEach((field) => {
    const raw = next[field];
    if (raw == null) {
      next[field] = "";
    } else if (typeof raw === "string") {
      next[field] = raw;
    } else if (typeof raw === "object") {
      // Bad legacy values (e.g. accidental {}) would render as blank otherwise
      next[field] = "";
    } else {
      next[field] = String(raw);
    }
  });
  Object.assign(next, unpackConnectSplashFromItem(next));
  Object.assign(next, unpackJoinInviteFromItem(next));
  const fillSplashExtras = isConnectSplashType(next.type);
  const fillJoinInviteExtras = isJoinInviteType(next.type);
  CONTENT_UI_EXTRA_FIELDS.forEach((field) => {
    const isSplashField = CONNECT_SPLASH_FIELD_KEYS.includes(field);
    const isJoinField = JOIN_INVITE_FIELD_KEYS.includes(field);
    if (isSplashField && !fillSplashExtras && !Object.hasOwn(next, field)) {
      return;
    }
    if (isJoinField && !fillJoinInviteExtras && !Object.hasOwn(next, field)) {
      return;
    }
    const raw = next[field];
    const booleanField =
      CONNECT_SPLASH_BOOLEAN_FIELDS.includes(field) ||
      JOIN_INVITE_BOOLEAN_FIELDS.includes(field);
    if (booleanField) {
      if (raw == null || raw === "") {
        next[field] =
          CONNECT_SPLASH_TRUE_DEFAULTS.has(field) ||
          JOIN_INVITE_TRUE_DEFAULTS.has(field)
            ? "true"
            : "false";
      } else {
        next[field] = isTruthyFlag(raw) ? "true" : "false";
      }
      return;
    }
    if (raw == null) {
      next[field] = "";
    } else if (typeof raw === "string") {
      next[field] = raw;
    } else if (typeof raw === "object") {
      next[field] = "";
    } else {
      next[field] = String(raw);
    }
  });
  return next;
};

export const formatDateDisplay = (value) => {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(value);
  }
};

export const downloadJson = (data, filename) => {
  const blob = new Blob([JSON.stringify(data, null, 2)], {
    type: "application/json",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
};

export const getResponseStatus = (res) => {
  if (!res) return 0;
  if (res.status) return res.status;
  if (res.response?.status) return res.response.status;
  return 0;
};

export const getResponseErrorMessage = (res) => {
  if (!res) return "Unknown error";
  if (res.response?.data?.message) return res.response.data.message;
  if (res.message) return res.message;
  if (res.response?.statusText) return res.response.statusText;
  return "Request failed";
};

export const toTitleCase = (str) => {
  if (!str) return "";
  return String(str)
    .replace(/([A-Z])/g, " $1")
    .replace(/[-_]/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Shorten long catalog identifiers (e.g. year-sha256) for UI display. */
export const formatShortIdentifier = (id, { head = 8, tail = 6 } = {}) => {
  if (id == null || id === "") return "";
  const s = String(id);
  if (s.length <= head + tail + 4) return s;
  const yearHash = s.match(/^(\d{4})-(.+)$/);
  if (yearHash) {
    const [, year, hash] = yearHash;
    if (hash.length > head + tail) {
      return `${year}-${hash.slice(0, head)}…${hash.slice(-tail)}`;
    }
  }
  return `${s.slice(0, head)}…${s.slice(-tail)}`;
};

export const SIDEBAR_STORAGE_KEY = "ms1-admin-sidebar-collapsed";
export const SIDEBAR_SECTION_KEY = "ms1-admin-last-section";
